import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Radio, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CollectionLiabilityEntry, getCollectionLiabilities } from '../../../api/collectionLiabilitiesApi';
import { payOffLiabilities } from '../../../api/paymentsApi';
import { useToast } from '../../../context/ToastContext';

const LIABILITIES_FETCH_SIZE = 200;

function formatAmount(amount: number): string {
  return `${amount.toFixed(2).replace('.', ',')} zł`;
}

export default function ExecutePaymentView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();

  const [liabilities, setLiabilities] = useState<CollectionLiabilityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getCollectionLiabilities({ statuses: ['PENDING'], size: LIABILITIES_FETCH_SIZE })
      .then((res) => setLiabilities(res.rows))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (initialSelectionApplied || loading) return;
    if (searchParams.get('selectAll') === 'true') {
      setSelectedIds(new Set(liabilities.map((liability) => liability.id)));
    } else {
      const liabilityId = searchParams.get('liabilityId');
      if (liabilityId && liabilities.some((liability) => liability.id === liabilityId)) {
        setSelectedIds(new Set([liabilityId]));
      }
    }
    setInitialSelectionApplied(true);
  }, [initialSelectionApplied, loading, liabilities, searchParams]);

  const totalCost = useMemo(
    () =>
      liabilities
        .filter((liability) => selectedIds.has(liability.id))
        .reduce((sum, liability) => sum + liability.collection.amount, 0),
    [liabilities, selectedIds],
  );

  function toggleSelected(liabilityId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(liabilityId)) next.delete(liabilityId);
      else next.add(liabilityId);
      return next;
    });
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const result = await payOffLiabilities({
        liabilities: Array.from(selectedIds),
        paymentMethod: 'TEST_PAYMENT',
      });
      if (result.isSuccess) {
        showToast(t('executePayment.successToast'));
        navigate('/parent/collections');
      } else {
        showToast(t('executePayment.errorToast'), 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ p: 4, width: '100%', flex: 1, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box
        sx={(theme) => ({
          flex: 2,
          borderRadius: 3,
          border: `1px solid ${theme.app.border}`,
          backgroundColor: theme.app.card,
          overflow: 'hidden',
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.app.borderSubtle}`,
          })}
        >
          <Typography variant="h6">{t('executePayment.listTitle')}</Typography>
          <Box
            sx={(theme) => ({
              px: 1.25,
              py: 0.25,
              borderRadius: 99,
              backgroundColor: theme.app.secondary,
            })}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {liabilities.length}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : liabilities.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              {t('executePayment.noDues')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 3, py: 2 }}>
            <TableHeader />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {liabilities.map((liability) => (
                <LiabilityRow
                  key={liability.id}
                  liability={liability}
                  selected={selectedIds.has(liability.id)}
                  onClick={() => toggleSelected(liability.id)}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={(theme) => ({
          flex: 1,
          minWidth: 320,
          borderRadius: 3,
          border: `1px solid ${theme.app.border}`,
          backgroundColor: theme.app.card,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        })}
      >
        <Typography variant="h6">{t('executePayment.selectMethod')}</Typography>
        <Box
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.app.borderStrong}`,
          })}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('executePayment.testPayment')}
          </Typography>
          <Radio checked disableRipple sx={{ p: 0 }} />
        </Box>

        <Box
          sx={(theme) => ({
            display: 'inline-block',
            alignSelf: 'flex-start',
            px: 1.25,
            py: 0.5,
            mt: 1,
            borderRadius: 99,
            backgroundColor: theme.app.infoSurface,
          })}
        >
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t('executePayment.summary')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t('executePayment.totalCost')}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {formatAmount(totalCost)}
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={selectedIds.size === 0 || submitting}
          onClick={handleSubmit}
          sx={(theme) => ({
            mt: 1,
            py: 1.25,
            borderRadius: 99,
            backgroundColor: theme.app.primary,
            color: theme.app.primaryForeground,
            '&:hover': { backgroundColor: theme.app.primaryHover },
          })}
        >
          {submitting ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : t('executePayment.submit')}
        </Button>
      </Box>
    </Box>
  );
}

function TableHeader() {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
      <Box sx={{ flex: '0 0 40px' }} />
      <Box sx={{ flex: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('executePayment.columnName')}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('executePayment.columnChild')}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'right' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {t('executePayment.columnAmount')}
        </Typography>
      </Box>
    </Box>
  );
}

function LiabilityRow({
  liability,
  selected,
  onClick,
}: {
  liability: CollectionLiabilityEntry;
  selected: boolean;
  onClick: () => void;
}) {
  const child = liability.payer.child;
  const childLabel = child ? `${child.firstName} ${child.lastName[0] ?? ''}.` : '-';

  return (
    <Box
      onClick={onClick}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        px: 1,
        borderRadius: 2,
        cursor: 'pointer',
        backgroundColor: selected ? theme.app.accentSurface : 'transparent',
        borderBottom: `1px solid ${theme.app.borderSubtle}`,
        '&:hover': { backgroundColor: selected ? theme.app.accentSurface : theme.app.secondary },
      })}
    >
      <Box sx={{ flex: '0 0 40px' }}>
        <Checkbox checked={selected} disableRipple sx={{ p: 0 }} />
      </Box>
      <Box sx={{ flex: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {liability.collection.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {liability.collection.organization?.name}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">{childLabel}</Typography>
      </Box>
      <Box sx={{ flex: 1, textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatAmount(liability.collection.amount)}
        </Typography>
      </Box>
    </Box>
  );
}
